import * as React from 'react';
import styles from './CouponPopup.scss';
import { useDispatch } from 'react-redux';
import { t } from '~/utils/localization';
import Coupon, { CouponSize } from '~/components/Coupon/Coupon';
import { openCategoryByName } from '~/utils/category';
import LabelDecorator, { LABEL_TYPE } from '~/decorators/LabelDecorator/LabelDecorator';
import Account from '~/account/Account';
import LabelContainer from '~/containers/LabelContainer/LabelContainer';
import { isValidDate } from '~/utils/time';
import { hasCoupons } from '~/utils/coupons';
import Button from '~/components/Button/Button';
import dwhExport from '~/api/dwhExport';
import { DWH_EVENTS, FILTER_DISCOUNT_NAME, FILTER_DISCOUNT_VALUES } from '~/const';
import { Popup, PopupBody, PopupFooter, PopupHeader } from '~/components/Popup/Popup';
import equal from 'fast-deep-equal/react';
import Attention from '~/components/Attention/Attention';
import useMaskScrollEffect from '~/hooks/useMaskScrollEffect';
import { RootState, useAppSelector } from '~/Store';
import { appActions } from '~/Store/appSlice';
import { POPUPS_NAME } from '~/components/PopupManager';
import { getAvailableFilters, hasCouponFilterFromCategory } from '~/utils/filters';

export const couponTip = t('Купон не действует на товары со скидкой и некоторые другие товары. ') + t('Нажмите на купон, чтобы просмотреть список товаров, на которые распространяется скидка.');

const stateSelector = (state: RootState) => {
    return {
        coupons: state.account.coupons,
        usedCoupons: state.account.usedCoupons,
        categories: state.app.categories,
        viewedLabels: state.account.viewedLabels,
    };
};

const CouponPopup = (): any => {
    const ref = React.useRef<HTMLDivElement>(null);
    const state = useAppSelector(stateSelector, equal);
    const dispatch = useDispatch();

    const close = () => dispatch(appActions.changeVisibilityPopup({ name: POPUPS_NAME.COUPON_POPUP }));

    const onClick = (category: ICategoryList, couponId: number) => {
        close();
        dwhExport.send(DWH_EVENTS.OPEN_CATEGORY_FROM_COUPON_POPUP, {
            coupon_id: couponId,
            category: category,
        });
        openCategoryByName(category, {
            place: 'coupon',
            couponId,
        });
    };

    const setRef = (_ref: HTMLDivElement) => {
        if (_ref) {
            ref.current = _ref;
        }
    };

    useMaskScrollEffect(ref);

    if (!hasCoupons()) {
        return (
            <Popup onClose={close} outsideClick={true} popupClassName={styles.popup}>
                <PopupHeader title={t('Нет доступных купонов')} />
                <PopupBody>
                    <div className={styles.couponEmptyWrapper}>{t('Попробуйте вернуться к купонам позднее.')}</div>
                </PopupBody>
                <PopupFooter>
                    <Button label={t('Закрыть')} onClick={close} />
                </PopupFooter>
            </Popup>
        );
    }

    return (
        <Popup onClose={close} popupClassName={styles.popup} className={styles.popupContent}>
            <PopupHeader title={t('Купоны')} className={styles.popupHeader} />
            <PopupBody className={styles.popupBody}>
                <div className={styles.couponsAboutText}>{t('Доступны специальные купоны, которые предоставляют скидку на предметы определенной категории в Адмиралтействе.')}</div>
                <div className={styles.couponsWrapper} ref={setRef}>
                    {state.coupons.map((coupon) => {
                        if (!state.categories?.[coupon.category]) {
                            return null;
                        }

                        const isAllowedNewLabel = isValidDate(coupon.labelNewActiveTill) && !Account.isViewedLabel(coupon.name) && !coupon.isDisabled;

                        const availableDiscountFilters = getAvailableFilters(coupon.category, FILTER_DISCOUNT_NAME);
                        const hasCouponFilter = hasCouponFilterFromCategory(state.categories[coupon.category]);

                        return (
                            <LabelDecorator
                                key={`coupon_${coupon.id}_${coupon.isDisabled}`}
                                id={coupon.name}
                                labelTill={coupon.labelNewActiveTill}
                                isEnabled={isAllowedNewLabel}
                                byHover
                                type={LABEL_TYPE.coupon}
                                className={styles.couponItem}
                            >
                                <Coupon
                                    coupon={coupon}
                                    size={CouponSize.BIG}
                                    onClick={() => {
                                        onClick(coupon.category, coupon.id);
                                    }}
                                    shownCurrencies
                                    isDeactivedCoupon={!coupon.isDisabled && hasCouponFilter && !availableDiscountFilters.includes(FILTER_DISCOUNT_VALUES.COUPON)}
                                />
                                <LabelContainer className={styles.label} isVisible={isAllowedNewLabel} />
                            </LabelDecorator>
                        );
                    })}
                </div>
                <Attention label={couponTip} level={'info'} isInline className={styles.attention} />
            </PopupBody>
            <PopupFooter className={styles.popupFooter}>
                <Button label={t('Закрыть')} onClick={close} className={'armory__auto--popup_close'} />
            </PopupFooter>
        </Popup>
    );
};

export default CouponPopup;
